import { useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  buildCalendarGrid,
  fmtIsoDate,
  parseIsoDate,
} from '../../lib/calendarGrid';

const WEEKDAYS_JA = ['日', '月', '火', '水', '木', '金', '土'] as const;

interface ViewMonth {
  y: number;
  m: number;
}

function todayParts(): { y: number; m: number; d: number } {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
}

/**
 * Asana-style month-grid date picker. Portal'd to document.body so the
 * popover can escape any parent `overflow-hidden` and isn't clipped to
 * a tight WBS cell. Anchored relative to `anchorEl`, with a flip-up /
 * flip-left fallback when the default placement would overflow the
 * viewport.
 *
 * Behaviour decisions:
 *   - The "view" month (what's drawn in the grid) is *independent* of
 *     the selected date. Users can browse forward/backward without
 *     committing — only an explicit day click commits.
 *   - Clicking ◀ / ▶ doesn't dismiss the popover. The native
 *     `<input type="date">` we previously embedded couldn't promise
 *     this (its overlay-window steals focus), which is the whole
 *     reason this component exists.
 *   - Outside-click / Esc are *not* handled here — the consumer owns
 *     dismissal so it can use a single rule that covers BOTH the
 *     trigger and this popover (`triggerRef.contains` ||
 *     `popoverRef.contains`).
 */
export interface DatePopoverProps {
  /** `YYYY-MM-DD` of the currently-selected day, or null/empty. */
  selected: string | null;
  /** Element used to position the popover (typically the trigger). */
  anchorEl: HTMLElement | null;
  /** Fired when the user clicks a day. */
  onPick: (iso: string) => void;
  /** Optional clear button in the footer. Hidden when not provided. */
  onClear?: () => void;
  /** Caller-owned ref — used for outside-click detection. */
  popoverRef: React.RefObject<HTMLDivElement>;
  /** Earliest selectable day, inclusive, `YYYY-MM-DD`. Days before are
   *  greyed and unclickable. Lex-compared (safe because the format is
   *  zero-padded). */
  min?: string;
  /** Latest selectable day, inclusive, `YYYY-MM-DD`. */
  max?: string;
}

export function DatePopover({
  selected,
  anchorEl,
  onPick,
  onClear,
  popoverRef,
  min,
  max,
}: DatePopoverProps) {
  const today = useMemo(todayParts, []);
  const [view, setView] = useState<ViewMonth>(() => {
    const parsed = selected ? parseIsoDate(selected) : null;
    if (parsed) return { y: parsed.y, m: parsed.m };
    return { y: today.y, m: today.m };
  });

  const cells = useMemo(() => buildCalendarGrid(view.y, view.m), [view]);

  // Position relative to anchor. Set initial guess immediately, then
  // refine after the popover has rendered (so we can flip when it
  // would overflow). `pos === null` skips the first render to avoid a
  // 0,0 flash.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const a = anchorEl.getBoundingClientRect();
    setPos({ top: a.bottom + 4, left: a.left });
  }, [anchorEl]);
  useLayoutEffect(() => {
    if (!anchorEl || !popoverRef.current) return;
    const a = anchorEl.getBoundingClientRect();
    const r = popoverRef.current.getBoundingClientRect();
    let top = a.bottom + 4;
    let left = a.left;
    if (r.right > window.innerWidth - 4) {
      left = Math.max(4, a.right - r.width);
    }
    if (r.bottom > window.innerHeight - 4) {
      top = Math.max(4, a.top - r.height - 4);
    }
    setPos((prev) =>
      prev && prev.top === top && prev.left === left ? prev : { top, left },
    );
  }, [anchorEl, popoverRef, view]);

  const monthLabel = `${view.y}年 ${view.m + 1}月`;
  const selectedKey = selected ?? '';
  const todayKey = fmtIsoDate(today.y, today.m, today.d);

  const stepMonth = (delta: number) => {
    setView((v) => {
      const next = v.m + delta;
      const yearShift = Math.floor(next / 12);
      // Modulo that handles negatives correctly.
      const m = ((next % 12) + 12) % 12;
      return { y: v.y + yearShift, m };
    });
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={popoverRef}
      data-date-popover
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        zIndex: 60,
        visibility: pos ? 'visible' : 'hidden',
      }}
      className="w-[260px] select-none rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl"
      // Stop propagation so clicks inside don't bubble to listeners
      // that close the editor (e.g. WBS row onClick).
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <header className="mb-1.5 flex items-center justify-between">
        <button
          type="button"
          onClick={() => stepMonth(-1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100"
          aria-label="前の月"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-sm font-semibold text-slate-800">{monthLabel}</div>
        <button
          type="button"
          onClick={() => stepMonth(1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100"
          aria-label="次の月"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </header>

      <div className="grid grid-cols-7">
        {WEEKDAYS_JA.map((w, i) => (
          <div
            key={w}
            className={cn(
              'flex h-6 items-center justify-center text-[10px] font-medium',
              i === 0
                ? 'text-rose-500'
                : i === 6
                ? 'text-sky-500'
                : 'text-slate-500',
            )}
          >
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((c, i) => {
          const k = fmtIsoDate(c.y, c.m, c.d);
          const isSelected = k === selectedKey;
          const isToday = k === todayKey;
          const outOfRange =
            (min !== undefined && min !== '' && k < min) ||
            (max !== undefined && max !== '' && k > max);
          return (
            <button
              key={`${k}-${i}`}
              type="button"
              disabled={outOfRange}
              aria-disabled={outOfRange || undefined}
              onClick={() => {
                if (outOfRange) return;
                onPick(k);
              }}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded text-[12px] transition-colors',
                'mx-auto',
                outOfRange
                  ? 'cursor-not-allowed text-slate-300 line-through opacity-50'
                  : isSelected
                  ? 'bg-sky-500 font-semibold text-white hover:bg-sky-600'
                  : c.inMonth
                  ? cn(
                      'hover:bg-slate-100',
                      c.dow === 0
                        ? 'text-rose-500'
                        : c.dow === 6
                        ? 'text-sky-500'
                        : 'text-slate-800',
                    )
                  : 'text-slate-300 hover:bg-slate-50',
                isToday && !isSelected && !outOfRange && 'ring-1 ring-sky-400',
              )}
              title={outOfRange ? `${k}(範囲外)` : k}
            >
              {c.d}
            </button>
          );
        })}
      </div>

      <footer className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
        {onClear ? (
          <button
            type="button"
            onClick={onClear}
            className="text-slate-500 hover:text-slate-800 hover:underline"
          >
            クリア
          </button>
        ) : (
          <span />
        )}
        {(() => {
          const todayOutOfRange =
            (min !== undefined && min !== '' && todayKey < min) ||
            (max !== undefined && max !== '' && todayKey > max);
          return (
            <button
              type="button"
              disabled={todayOutOfRange}
              onClick={() => {
                if (todayOutOfRange) return;
                setView({ y: today.y, m: today.m });
                onPick(todayKey);
              }}
              className={cn(
                'font-medium',
                todayOutOfRange
                  ? 'cursor-not-allowed text-slate-300'
                  : 'text-sky-600 hover:text-sky-700 hover:underline',
              )}
            >
              今日
            </button>
          );
        })()}
      </footer>
    </div>,
    document.body,
  );
}

